import Box from '@/common/components/Box'
import BoxWithHeader from '@/common/components/BoxWithHeader'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ShowHeader } from '@/common/components/ShowHeader'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { fiscalAssessmentDocumentGroupsKind } from '../../../../constants/forwardings/steps/fiscalAssessmentEntry/fiscalAssessmentDocumentGroupsKind.constant'
import { useMaintenanceFiscalAssessmentSimpleEntriesAdvance } from '../../../../hooks/forwardings/establishmentChoice/fiscalAssessment/simpleEntries/useMaintenanceFiscalAssessmentSimpleEntriesAdvance'
import { ForwardingTabContext } from '../../../../hooks/forwardings/useForwardingTabContext'
import { useMaintenanceForwarding } from '../../../../hooks/forwardings/useMaintenanceForwarding'
import FiscalAssessmentDocumentGroupDocument from '../../../../pages/Show/components/tabs/ForwardingsTab/sections/FiscalAssessmentEntry/FiscalAssessmentDocumentGroupDocument'
import FiscalAssessmentEntryUpdateDocumentsAppend from '../../../../pages/Show/components/tabs/ForwardingsTab/sections/FiscalAssessmentEntry/UpdateDocuments'
import { MaintenanceFiscalAssessmentEntryStatus } from '../../../../types/forwardings/fiscalAssessmentEntry/fiscalAssessmentEntry.type'
import MaintenanceWorkflowFiscalAssessmentEntryUpdateLoading from './components/MaintenanceWorkflowFiscalAssessmentEntryUpdateLoading'

const MaintenanceWorkflowFiscalAssessmentEntryUpdate = () => {
  const { workflowId, forwardingId } = useParams()

  const { isUserPermissionsPending } = useUserPermissions()

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const {
    data: forwarding,
    isPending: forwardingIsPending,
    error: forwardingError = {} as any,
    refetch: refetchForwarding,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding(forwardingId)

  const { mutate: advance, isPending: advanceIsPending } = useMaintenanceFiscalAssessmentSimpleEntriesAdvance({
    fiscalAssessmentEntryId: forwarding?.fiscal_assessment_entry?.id || '',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] })
        navigate(`/maintenance-workflows/${workflowId}/Maintenance::Directive::ExecutionEntry`, { replace: true })
      },
    },
  })

  const isLoading = useMemo(() => {
    return isUserPermissionsPending || forwardingIsPending
  }, [isUserPermissionsPending, forwardingIsPending])

  const isAwaitingEstablishmentDocuments = useMemo(() => {
    return (
      forwarding?.fiscal_assessment_entry?.status ===
      MaintenanceFiscalAssessmentEntryStatus.AwaitingEstablishmentDocuments
    )
  }, [forwardingUpdatedAt])

  const everyDocumentGroupHasDocument = useMemo(() => {
    return forwarding?.fiscal_assessment_entry?.document_groups?.every(
      (documentGroup) => Number(documentGroup.documents?.length) > 0,
    )
  }, [forwardingUpdatedAt])

  useEffect(() => {
    if (
      forwarding?.fiscal_assessment_entry?.status === MaintenanceFiscalAssessmentEntryStatus.AwaitingManagementReview
    ) {
      navigate(`/maintenance-workflows/${workflowId}Maintenance::Directive::ExecutionEntry`, { replace: true })
    }
  }, [forwardingUpdatedAt])

  return (
    <div className="inner-screen">
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/maintenance-workflows/${workflowId}/Maintenance::Directive::ExecutionEntry`,
          searchLastRouteFromFallback: true,
        }}
        isLoading={isLoading}
        title="Recolha de notas"
        titleClassName="text-lg font-medium"
        buttons={
          !isLoading &&
          isAwaitingEstablishmentDocuments && (
            <Button
              disabled={!everyDocumentGroupHasDocument || advanceIsPending}
              operationKlassToCheckPermission="Establishment::Maintenance::FiscalAssessment::SimpleEntries::Advance"
              onClick={() => advance()}
              isLoading={advanceIsPending}
            >
              Enviar notas
            </Button>
          )
        }
      />

      {isLoading && <MaintenanceWorkflowFiscalAssessmentEntryUpdateLoading />}

      {!isLoading && forwarding && (
        <ErrorMessageWrapper
          className="flex-1"
          statusCode={forwardingError?.status}
          refetchFunction={refetchForwarding}
        >
          <ForwardingTabContext.Provider
            value={{
              forwarding,
              forwardingUpdatedAt,
              directiveEntryKlass: undefined,
            }}
          >
            <div className="flex-1 flex gap-6 overflow-hidden">
              <div className="flex-1 overflow-auto">
                <Box className="p-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-3">
                    {forwarding?.fiscal_assessment_entry?.document_groups?.map((documentGroup, index) => (
                      <React.Fragment key={documentGroup.id}>
                        {index > 0 && <div className="h-px bg-white-200" />}

                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <p>{fiscalAssessmentDocumentGroupsKind[documentGroup.kind] || documentGroup.kind}</p>
                            </div>

                            {isAwaitingEstablishmentDocuments && (
                              <FiscalAssessmentEntryUpdateDocumentsAppend
                                documentGroup={documentGroup}
                                disabled={advanceIsPending}
                              />
                            )}
                          </div>

                          {Number(documentGroup.documents.length) > 0 && (
                            <div className="flex flex-col xl:grid xl:grid-cols-2 flex-wrap gap-2">
                              {documentGroup.documents.map((document) => (
                                <FiscalAssessmentDocumentGroupDocument
                                  key={document.id}
                                  documentGroupId={documentGroup.id}
                                  document={document}
                                  isAwaitingEstablishmentDocuments={isAwaitingEstablishmentDocuments}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </Box>
              </div>

              <div>
                <BoxWithHeader
                  title="Checklist"
                  subtitle={forwarding?.fiscal_assessment_entry?.requirement_list_summary?.description}
                >
                  <div className="flex flex-col gap-2">
                    {forwarding?.fiscal_assessment_entry?.requirement_list_summary?.steps?.map((step) => (
                      <Checkbox
                        key={step.name}
                        label={step.name}
                        name={step.name.replace(/\s/g, '_').toLowerCase()}
                        checked={step.passed}
                        disabled
                        labelClassName="italic text-xs"
                      />
                    ))}
                  </div>

                  {isAwaitingEstablishmentDocuments && (
                    <Button
                      disabled={!everyDocumentGroupHasDocument || advanceIsPending}
                      operationKlassToCheckPermission="Establishment::Maintenance::FiscalAssessment::SimpleEntries::Advance"
                      onClick={() => advance()}
                      isLoading={advanceIsPending}
                    >
                      Enviar notas
                    </Button>
                  )}
                </BoxWithHeader>
              </div>
            </div>
          </ForwardingTabContext.Provider>
        </ErrorMessageWrapper>
      )}
    </div>
  )
}

export default MaintenanceWorkflowFiscalAssessmentEntryUpdate
